'use client'

import type { VariantProps } from 'class-variance-authority'
import { forwardRef, useId, useState, type InputHTMLAttributes } from 'react'
import { cn } from '../../utils/cn'
import { ADORNMENT_ICON, adornmentVariants } from './adornment'
import { controlVariants } from './control'
import { useFieldControlProps } from './field'

/**
 * A password field with a show/hide toggle.
 *
 * Three things changed from the donor.
 *
 * 1. The toggle was an icon-only button with no accessible name, announced as
 *    "button" and impossible to act on without sight. Its name is now required
 *    copy, and it flips with the state so the button always says what pressing
 *    it will do.
 * 2. The donor spread the caller's props after its own `type`, so any caller
 *    passing `type` disabled the toggle outright. `type` is not a prop here.
 * 3. The donor's adornment button carried no `type`, which inside a form makes the
 *    default `submit`: revealing your password submitted the login form. Same
 *    bug the reconciliation rows had. It is `type="button"`.
 */

export type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'style' | 'size' | 'type'> &
    VariantProps<typeof controlVariants> & {
        /** Names the toggle while the password is hidden. */
        showLabel: string
        /** Names the toggle while the password is visible. */
        hideLabel: string
    }

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
    { size, showLabel, hideLabel, ...props },
    ref
) {
    const fieldProps = useFieldControlProps(props)
    const controlSize = size ?? 'medium'

    const [isVisible, setIsVisible] = useState(false)

    // The toggle points at the input with aria-controls, which needs a real id
    // even when there is no Field to hand one down.
    const fallbackId = useId()
    const id = fieldProps.id ?? fallbackId

    return (
        <div className="relative w-full">
            <input
                {...props}
                ref={ref}
                {...fieldProps}
                type={isVisible ? 'text' : 'password'}
                id={id}
                className={cn(controlVariants({ size, hasEndAdornment: true }))}
            />

            <button
                type="button"
                onClick={() => setIsVisible((visible) => !visible)}
                // A toggle on a control nobody can type into is a control that
                // reveals a value nobody can change.
                disabled={props.disabled}
                aria-controls={id}
                aria-label={isVisible ? hideLabel : showLabel}
                className={cn(adornmentVariants({ size: controlSize, isInteractive: true }))}
            >
                <svg viewBox="0 0 16 16" className={ADORNMENT_ICON[controlSize]} aria-hidden focusable="false">
                    <path
                        d="M1 8s2.6-4.5 7-4.5S15 8 15 8s-2.6 4.5-7 4.5S1 8 1 8Z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                    />
                    <circle cx="8" cy="8" r="1.75" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    {isVisible ? (
                        <path
                            d="M2.5 2.5 13.5 13.5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                        />
                    ) : null}
                </svg>
            </button>
        </div>
    )
})
